/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { toast } from "../components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { regionLabel } from "../data/crm";
import { ClipboardCheck, Sparkles, Copy, ExternalLink, Loader2, Inbox } from "lucide-react";

function ReviewCard({ lead, zh, api, onDone, nav, canReview, regions }) {
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [script, setScript] = useState("");
  const [gen, setGen] = useState(false);
  const decide = async (decision) => {
    setBusy(decision);
    try { await api.post(`/crm/leads/${lead.id}/mm-review-decision`, { decision, note });
      toast.success(zh ? "已完成复核归类" : "Reviewed"); onDone(); }
    catch (e) { toast.error(e?.detailText || (zh ? "操作失败" : "Failed")); setBusy(""); }
  };
  const genScript = async () => {
    setGen(true);
    try { const { data } = await api.post(`/crm/leads/${lead.id}/review-script`, { channel: zh ? "电话" : "phone" }); setScript(data.script || ""); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "AI 生成失败" : "AI failed")); }
    finally { setGen(false); }
  };
  const opts = [
    { k: "invalid", zh: "定为无效", en: "Invalid", cls: "bg-rose-600 hover:bg-rose-700" },
    { k: "nurturing", zh: "转入培育", en: "Nurture", cls: "bg-slate-500 hover:bg-slate-600" },
    { k: "pool", zh: "退回公海", en: "To Pool", cls: "bg-sky-600 hover:bg-sky-700" },
    { k: "reactivate", zh: "重新激活", en: "Reactivate", cls: "bg-emerald-600 hover:bg-emerald-700" },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`review-card-${lead.id}`}>
      <div className="flex-wrap md:flex-nowrap flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-slate-800 truncate">{lead.clientName || (zh ? "(未命名)" : "(Unnamed)")}</p>
          <p className="text-[12px] text-slate-500 mt-0.5">
            {regionLabel(regions, lead.region || lead.branch, zh)} · {lead.city || lead.address || (zh ? "无地址" : "No address")}
            {lead.phone && <span className="ml-1">· {lead.phone}</span>}
          </p>
        </div>
        <button onClick={() => nav(`/crm/leads/${lead.id}`)} data-testid={`review-open-${lead.id}`}
          className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 shrink-0 text-[12px] font-semibold text-indigo-600 inline-flex items-center gap-1 hover:underline">
          {zh ? "详情" : "Open"}<ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {lead.lostReason && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100">{zh ? "备注：" : "Note: "}{lead.lostReason}</span>}
        {lead.lostFlaggedBy && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-100">{zh ? "标记人：" : "By: "}{lead.lostFlaggedBy}</span>}
      </div>
      {canReview && <><button data-testid={`review-gen-script-${lead.id}`} onClick={genScript} disabled={gen}
        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 mt-2.5 h-8 px-3 rounded-lg text-sm font-semibold bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50 disabled:opacity-50 inline-flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5" />{gen ? (zh ? "生成中…" : "…") : (zh ? "AI 生成复核话术" : "AI review script")}
      </button>
      {script && (
        <div className="mt-2 rounded-lg bg-indigo-50/60 border border-indigo-100 p-2.5" data-testid={`review-script-${lead.id}`}>
          <p className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{script}</p>
          <button onClick={() => { navigator.clipboard?.writeText(script); toast.success(zh ? "已复制" : "Copied"); }}
            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 mt-1.5 text-[12px] font-semibold text-indigo-600 inline-flex items-center gap-1"><Copy className="h-3 w-3" />{zh ? "复制话术" : "Copy"}</button>
        </div>
      )}
      <input data-testid={`review-note-${lead.id}`} value={note} onChange={(e) => setNote(e.target.value)}
        aria-label={zh ? "复核备注（可选）" : "Review note (optional)"}
        placeholder={zh ? "复核备注(可选)" : "Review note (optional)"}
        className="mt-2.5 w-full h-8 text-sm rounded-lg border border-slate-200 px-2" />
      <div className="mt-2 flex flex-wrap gap-2">
        {opts.map((o) => (
          <button key={o.k} data-testid={`review-decide-${o.k}-${lead.id}`} disabled={!!busy} onClick={() => decide(o.k)}
            className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`h-8 px-3 rounded-lg text-white text-sm font-semibold ${o.cls} disabled:opacity-50`)}>
            {busy === o.k ? "…" : (zh ? o.zh : o.en)}
          </button>
        ))}
      </div></>}
    </div>
  );
}

export default function CRMReviewQueue() {
  const { api, lang, user, permOf } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [scope, setScope] = useState(null);
  const [scopeError, setScopeError] = useState(false);
  const [scopeAttempt, setScopeAttempt] = useState(0);
  const [region, setRegion] = useState("__all__");
  const [revision, setRevision] = useState(0);
  const [leads, setLeads] = useState(null);
  const [leadsError, setLeadsError] = useState(false);
  const [kpi, setKpi] = useState(null);
  const [kpiError, setKpiError] = useState(false);
  const canReview = permOf("/crm/mm-review::review") === true;

  useEffect(() => {
    let current = true;
    setScope(null);
    setScopeError(false);
    setLeads(null);
    setKpi(null);
    setRegion("__all__");
    api.get("/crm/mm-review/scope").then(({ data }) => {
      if (current) setScope(data);
    }).catch((error) => { if (current) setScopeError(error?.response?.status === 403 ? "forbidden" : "unavailable"); });
    return () => { current = false; };
  }, [api, scopeAttempt, user?.username]);

  useEffect(() => {
    if (!scope) return undefined;
    let current = true;
    const params = scope.canChooseBranch && region !== "__all__" ? { region } : {};
    setLeads(null);
    setLeadsError(false);
    setKpi(null);
    setKpiError(false);
    api.get("/crm/mm-review", { params }).then(({ data }) => {
      if (current) setLeads(data || []);
    }).catch(() => { if (current) setLeadsError(true); });
    api.get("/crm/review-kpi", { params }).then(({ data }) => {
      if (current) setKpi(data);
    }).catch(() => { if (current) setKpiError(true); });
    return () => { current = false; };
  }, [api, scope, region, revision]);
  const refresh = () => setRevision((value) => value + 1);

  const fmtH = (h) => h >= 24 ? `${(h / 24).toFixed(1)}${zh ? "天" : "d"}` : `${h}${zh ? "小时" : "h"}`;

  return (
    <div className="max-w-5xl mx-auto" data-testid="crm-review-queue">
      <div className="flex-wrap md:flex-nowrap flex items-center gap-2.5 mb-1">
        <div className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center"><ClipboardCheck className="h-5 w-5 text-amber-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{zh ? "流失待复核" : "Churn Review Queue"}</h1>
          <p className="text-[13px] text-slate-500">{zh ? "销售标记流失的客户，请联系确认后做最终归类。" : "Leads flagged as lost by sales — review & classify."}</p>
        </div>
      </div>

      {scope && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="review-branch-scope">
          {scope.canChooseBranch ? <>
            <label htmlFor="review-branch-select" className="text-sm font-medium text-slate-700">{zh ? "分公司" : "Branch"}</label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger id="review-branch-select" data-testid="review-branch-select" className="w-full sm:w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{zh ? "全部分公司" : "All branches"}</SelectItem>
                {(scope.branches || []).map((branch) => <SelectItem key={branch.id} value={branch.id}>{zh ? branch.zh : branch.en}</SelectItem>)}
              </SelectContent>
            </Select>
          </> : <p className="text-sm text-slate-600" data-testid="review-branch-locked">
            {zh ? "所属分公司：" : "Branch: "}{regionLabel(scope.branches, scope.region, zh)}
          </p>}
        </div>
      )}

      {scopeError && <div role="alert" className="mt-4 text-sm text-rose-700">
        {scopeError === "forbidden"
          ? (zh ? "当前账号没有复核权限或未设置所属分公司，请联系管理员。" : "Your account lacks review access or a branch. Contact an administrator.")
          : (zh ? "复核范围加载失败。" : "Unable to load review scope.")}{" "}
        <button type="button" className="font-semibold underline focus-visible:ring-2 focus-visible:ring-brand-400" onClick={() => setScopeAttempt((value) => value + 1)}>{zh ? "重试" : "Retry"}</button>
      </div>}

      {kpiError && <div role="alert" className="mt-4 text-sm text-rose-700">
        {zh ? "复核统计加载失败。" : "Unable to load review statistics."}{" "}
        <button type="button" className="font-semibold underline focus-visible:ring-2 focus-visible:ring-brand-400" onClick={refresh}>{zh ? "重试" : "Retry"}</button>
      </div>}

      {kpi && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="review-kpi-panel">
          <p className="text-[14px] font-bold text-slate-700 mb-3">{zh ? "复核时效考核" : "Review Timeliness"}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div className="rounded-xl bg-amber-50 p-3" data-testid="kpi-pending"><p className="text-2xl font-black text-amber-600">{kpi.pending}</p><p className="text-[11px] text-slate-500">{zh ? "待处理积压" : "Pending"}</p></div>
            <div className="rounded-xl bg-rose-50 p-3" data-testid="kpi-oldest"><p className="text-2xl font-black text-rose-600">{fmtH(kpi.oldestPendingHours)}</p><p className="text-[11px] text-slate-500">{zh ? "最久未处理" : "Oldest pending"}</p></div>
            <div className="rounded-xl bg-indigo-50 p-3" data-testid="kpi-avg"><p className="text-2xl font-black text-indigo-600">{fmtH(kpi.avgHoursOverall)}</p><p className="text-[11px] text-slate-500">{zh ? "平均处理时长" : "Avg handle time"}</p></div>
            <div className="rounded-xl bg-emerald-50 p-3" data-testid="kpi-handled"><p className="text-2xl font-black text-emerald-600">{kpi.totalHandled}</p><p className="text-[11px] text-slate-500">{zh ? "累计已处理" : "Total handled"}</p></div>
          </div>
          {kpi.ranking && kpi.ranking.length > 0 && (
            <ResponsiveTable className="w-full text-sm" data-testid="kpi-ranking">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-1.5 pr-3">#</th><th className="py-1.5 pr-3">{zh ? "市场部经理" : "Reviewer"}</th>
                <th className="py-1.5 pr-3">{zh ? "已处理" : "Handled"}</th><th className="py-1.5 pr-3">{zh ? "平均耗时" : "Avg time"}</th></tr></thead>
              <tbody>
                {kpi.ranking.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`kpi-row-${i}`}>
                    <td className="py-1.5 pr-3 font-bold text-slate-400">{i + 1}</td>
                    <td className="py-1.5 pr-3 font-semibold text-slate-700">{r.reviewer}</td>
                    <td className="py-1.5 pr-3 text-slate-500">{r.handled}</td>
                    <td className={`py-1.5 pr-3 font-semibold ${r.avgHours <= 24 ? "text-emerald-600" : "text-rose-600"}`}>{fmtH(r.avgHours)}</td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          )}
        </div>
      )}

      {scopeError ? null : leadsError ? (
        <div role="alert" className="mt-4 text-sm text-rose-700" data-testid="review-load-error">
          {zh ? "待复核客户加载失败。" : "Unable to load customers for review."}{" "}
          <button type="button" className="font-semibold underline focus-visible:ring-2 focus-visible:ring-brand-400" onClick={refresh}>{zh ? "重试" : "Retry"}</button>
        </div>
      ) : leads === null ? (
        <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400" data-testid="review-empty">
          <Inbox className="h-10 w-10 mb-2" /><p className="text-sm">{zh ? "暂无待复核客户" : "No leads to review"}</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 mt-4">
          {leads.map((l) => <ReviewCard key={l.id} lead={l} zh={zh} api={api} nav={nav} onDone={refresh} canReview={canReview} regions={scope?.branches || []} />)}
        </div>
      )}
    </div>
  );
}
